import type { MetabaseQuestion } from "@/client/__generated__/types.gen";

// A question's identity on a dashboard, and everything a select option value encodes.
export interface QuestionRef {
  id: number;
  dashcardId: number;
  type: string;
}

// A question resolved back against the dashboard metadata it came from.
export interface ParsedQuestion extends QuestionRef {
  // The underlying saved question name (Metabase's card.name).
  name: string;
  // What the dashboard displays for this card, i.e. its card.title override or card.name.
  title: string;
}

// Option values carry ids only. They used to embed the question name, which is free text, so the
// `_`-delimited round trip corrupted both the name and the id for any card whose name contains an
// underscore. Names now come from the metadata instead of the option value.
export const questionToString = (question: QuestionRef): string =>
  `${question.id}_${question.dashcardId}_${question.type}`;

export const parseQuestionRef = (value: string): QuestionRef => {
  const [id, dashcardId, type] = value.split("_");
  return {
    id: parseInt(id),
    dashcardId: parseInt(dashcardId),
    type,
  };
};

const toParsedQuestion = (question: MetabaseQuestion): ParsedQuestion => ({
  id: question.id,
  dashcardId: question.dashcardId,
  type: question.type,
  name: question.name,
  title: question.title ?? question.name,
});

/**
 * Resolves an option value against the dashboard metadata it was built from. Returns null when the
 * question is no longer on the dashboard, so stale form state cannot be submitted.
 */
export const resolveQuestion = (
  value: string | undefined,
  questions: MetabaseQuestion[] | undefined,
): ParsedQuestion | null => {
  if (!value) return null;
  const ref = parseQuestionRef(value);
  const match = (questions ?? []).find(
    (question) =>
      question.id === ref.id && question.dashcardId === ref.dashcardId,
  );
  return match ? toParsedQuestion(match) : null;
};

export const resolveQuestions = (
  values: string[] | undefined,
  questions: MetabaseQuestion[] | undefined,
): ParsedQuestion[] =>
  (values ?? [])
    .map((value) => resolveQuestion(value, questions))
    .filter((question): question is ParsedQuestion => question !== null);

// Labelled with the dashboard title so the list matches what users see on the report itself.
export const questionsToOptions = (questions: MetabaseQuestion[]) =>
  questions.map((question) => ({
    value: questionToString(question),
    label: question.title ?? question.name,
  }));
